import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getShortlistPresets,
	createShortlistPreset,
	updateShortlistPreset,
	deleteShortlistPreset,
} from "../api/shortlist-report-api";
import type { ShortlistPreset, CreatePresetPayload, UpdatePresetPayload } from "../api/shortlist-report-api";
import { showToast } from "utils/helpers";

const PRESETS_KEY = "shortlist-presets";

export const useShortlistPresets = () => {
	return useQuery<ShortlistPreset[]>({
		queryKey: [PRESETS_KEY],
		queryFn: getShortlistPresets,
		staleTime: 5 * 60 * 1000,
	});
};

export const useCreatePreset = () => {
	const queryClient = useQueryClient();

	return useMutation<ShortlistPreset, Error, CreatePresetPayload>({
		mutationFn: createShortlistPreset,
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: [PRESETS_KEY] });
			showToast(`Preset "${data.name}" saved`, "Success", true);
		},
		onError: (error) => {
			showToast(error.message || "Failed to save preset", "Error");
		},
	});
};

export const useUpdatePreset = () => {
	const queryClient = useQueryClient();

	return useMutation<ShortlistPreset, Error, { uuid: string; payload: UpdatePresetPayload }>({
		mutationFn: ({ uuid, payload }) => updateShortlistPreset(uuid, payload),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: [PRESETS_KEY] });
			showToast(`Preset "${data.name}" updated`, "Success", true);
		},
		onError: (error) => {
			showToast(error.message || "Failed to update preset", "Error");
		},
	});
};

export const useDeletePreset = () => {
	const queryClient = useQueryClient();

	return useMutation<void, Error, string>({
		mutationFn: deleteShortlistPreset,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [PRESETS_KEY] });
			showToast("Preset deleted", "Success", true);
		},
		onError: (error) => {
			showToast(error.message || "Failed to delete preset", "Error");
		},
	});
};
